import React, {
  MouseEvent,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import {
  useNavigate,
} from 'react-router-dom';
import {
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  useMedia,
} from 'core/hooks';
import {
  PCCDashboardServiceDTO,
} from 'dtos';
import Name from 'pages/Dashboard/components/Name';
import {
  getPCCStatusPresentation,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/table/constants';
import PCCAlertActions, {
  isSupportedPCCAlertAction,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/alerts/PCCAlertActions';
import {
  getEligiblePCCPrograms,
  normalizeAlerts,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/helper';
import PCCProgramStatusPill from 'pages/Dashboard/pages/Dashboard/PCCOverview/table/PCCProgramStatusPill';
import {
  PCCOverviewRow,
  PCCPatientActionContext,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/types';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
 getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';

type PatientCellProps = {
  row: Row<PCCOverviewRow>;
};

type ProgramStatusCellProps = {
  service?: PCCDashboardServiceDTO;
};

type ProgramColumnId = 'rtm' | 'ccm' | 'tempo';

function PatientCell({ row }: PatientCellProps) {
  const { getMediaPathById } = useMedia();
  const navigate = useNavigate();
  const src = getMediaPathById(row.original.patient?.patientId ?? 0) ?? '';
  const link = getPatientChartPath(row.original.patient?.patientId ?? 0);

  return (
    <div className="w-full !truncate">
      <Name
        {...row.original.patient?.formattedNames}
        placeholder={DEFAULT_EMPTY_VALUE}
        image={{ src }}
        link={{
          to: link,
          onClick: (event: MouseEvent<HTMLAnchorElement>) => {
            event.stopPropagation();
            if (!isNil(link)) {
              navigate(link);
            }
          },
        }}
       />
    </div>
  );
}

function getProgramStatusLabel(service?: PCCDashboardServiceDTO): string {
  if (isNil(service)) {
    return '';
  }

  return getPCCStatusPresentation(
    service.status,
    Boolean(service.isOptedOut),
  ).label;
}

function ProgramStatusCell({ service }: ProgramStatusCellProps) {
  const hasService = !isNil(service);
  const alertLabels = normalizeAlerts(service?.alerts);
  const minutesRemaining = service?.minutesRemaining;

  return hasService ? (
    <div className="flex flex-col items-start gap-0.5">
      <PCCProgramStatusPill
        status={service?.status}
        isOptedOut={service?.isOptedOut}
      />
      {alertLabels.map(({ alert, key }) => notEmptyString(alert.subtext) && (
        <span
          key={key}
          className="text-xs font-medium leading-4 text-gray-500"
        >
          {alert.subtext}
        </span>
      ))}
      {!isNilOrZero(minutesRemaining) && (
        <span className="text-xs font-medium leading-4 text-gray-500">
          {minutesRemaining} of 20 minutes remaining
        </span>
      )}
    </div>
  ) : (
    <span className="text-xs font-medium text-gray-700">{DEFAULT_EMPTY_VALUE}</span>
  );
}

function getProgramColumn(
  id: ProgramColumnId,
  header: string,
): ColumnDef<PCCOverviewRow> {
  return {
    id,
    accessorFn: (row) => getProgramStatusLabel(row[id]),
    header,
    minSize: 160,
    meta: { autoSized: true },
    cell: ({ row }) => (
      <ProgramStatusCell service={row.original[id]} />
    ),
  };
}

function getPatientActionContext(row: PCCOverviewRow): PCCPatientActionContext {
  return {
    patientId: row.patient?.patientId ?? 0,
    patientPhone: row.patient?.phone,
    patientEmail: row.patient?.email,
    programs: getEligiblePCCPrograms(row),
  };
}

function getActionLabels(row: PCCOverviewRow): string {
  return (row.alerts ?? [])
    .flatMap((alert) => alert.availableActions ?? [])
    .filter(isSupportedPCCAlertAction)
    .map((action) => action.label ?? '')
    .join(' ');
}

export default function getColumns(
  isCcmApplicable: boolean = true,
): ColumnDef<PCCOverviewRow>[] {
  const programColumns = isCcmApplicable
    ? [
      getProgramColumn('ccm', 'CCM'),
      getProgramColumn('rtm', 'RTM'),
      getProgramColumn('tempo', 'Tempo'),
    ]
    : [
      getProgramColumn('rtm', 'RTM'),
      getProgramColumn('tempo', 'Tempo'),
    ];

  return [{
    id: 'patient',
    accessorFn: (row) => getDisplayName(row.patient?.formattedNames),
    header: 'Patient',
    minSize: 220,
    maxSize: 320,
    meta: { autoSized: true },
    cell: ({ row }) => <PatientCell row={row} />,
  },
  ...programColumns,
  {
    id: 'action',
    accessorFn: getActionLabels,
    header: '',
    minSize: 180,
    meta: { autoSized: true },
    cell: ({ row }) => (
      <PCCAlertActions
        alerts={row.original.alerts}
        patient={getPatientActionContext(row.original)}
      />
    ),
  }];
}
